import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Bell, ArrowLeft, Check, CheckCheck, Trash2, MessageSquare, FileText, 
  FolderOpen, Clock, Calculator, Layers, Eye, Settings, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import HelpIcon from '@/components/HelpIcon';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Notification type icons and colors
const NOTIFICATION_CONFIG = {
  message: { 
    icon: MessageSquare, 
    color: 'text-blue-500', 
    bgColor: 'bg-blue-100 dark:bg-blue-900/30' 
  },
  document_signed: { 
    icon: FileText, 
    color: 'text-green-500', 
    bgColor: 'bg-green-100 dark:bg-green-900/30' 
  },
  project_update: { 
    icon: FolderOpen, 
    color: 'text-purple-500', 
    bgColor: 'bg-purple-100 dark:bg-purple-900/30' 
  },
  task_due: { 
    icon: Clock, 
    color: 'text-orange-500', 
    bgColor: 'bg-orange-100 dark:bg-orange-900/30' 
  },
  estimate_created: { 
    icon: Calculator, 
    color: 'text-emerald-500', 
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/30' 
  },
  clip_added: { 
    icon: Layers, 
    color: 'text-pink-500', 
    bgColor: 'bg-pink-100 dark:bg-pink-900/30' 
  },
  portal_accessed: { 
    icon: Eye, 
    color: 'text-cyan-500', 
    bgColor: 'bg-cyan-100 dark:bg-cyan-900/30' 
  },
  system: { 
    icon: Settings, 
    color: 'text-gray-500', 
    bgColor: 'bg-gray-100 dark:bg-gray-800' 
  }
};

function formatTimeAgo(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);
  
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
  return date.toLocaleDateString();
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, read
  
  // Dark mode - synced with app's keyMadeDarkMode
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('keyMadeDarkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('keyMadeDarkMode');
      setDarkMode(saved ? JSON.parse(saved) : false);
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Failed to mark all as read');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success('Notification deleted');
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error('Failed to delete notification');
    }
  };

  const clearAllNotifications = async () => {
    if (!window.confirm('Are you sure you want to delete all notifications?')) return;
    
    try {
      await axios.delete(`${API}/notifications`);
      setNotifications([]);
      setUnreadCount(0);
      toast.success('All notifications cleared');
    } catch (error) {
      console.error('Error clearing notifications:', error);
      toast.error('Failed to clear notifications');
    }
  };

  const handleNotificationClick = (notification) => {
    if (!notification.read) {
      markAsRead(notification.id);
    }
    
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const config = (type) => NOTIFICATION_CONFIG[type] || NOTIFICATION_CONFIG.system;

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'unread') return !n.read;
    if (filter === 'read') return n.read;
    return true;
  });

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-[#0D0D0D]' : 'bg-[#F5F1ED]'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 ${darkMode ? 'bg-[#0D0D0D] border-b border-[#1A1A1A]' : 'bg-white border-b'}`}>
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/dashboard')}
                className={darkMode ? 'text-[#E5E2DD] hover:bg-[#1A1A1A]' : ''}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <HelpIcon elementId="notifications-page" position="inline" size="md" />
                <Bell className={`w-6 h-6 ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`} />
                <div>
                  <h1 className={`text-xl font-heading ${darkMode ? 'text-white' : ''}`}>
                    Notifications
                  </h1>
                  <p className={`text-sm ${darkMode ? 'text-[#8A8A8A]' : 'text-gray-500'}`}>
                    {unreadCount} unread
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={markAllAsRead}
                  className={darkMode ? 'border-[#2A2A2A] hover:bg-[#1A1A1A]' : ''}
                >
                  <CheckCheck className="w-4 h-4 mr-2" />
                  Mark all read
                </Button>
              )}
              {notifications.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={clearAllNotifications}
                  className={`text-red-500 hover:text-red-600 ${darkMode ? 'border-[#2A2A2A] hover:bg-red-900/20' : 'hover:bg-red-50'}`}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear all
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Breadcrumbs */}
      <div className={`border-b ${darkMode ? 'border-[#1A1A1A] bg-[#0D0D0D]' : 'bg-white'}`}>
        <div className="max-w-4xl mx-auto px-6 py-2">
          <div className="flex items-center gap-2 text-sm">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className={darkMode ? 'hover:bg-[#1A1A1A] text-[#8A8A8A]' : ''}>
              My Desk
            </Button>
            <span className={darkMode ? 'text-[#6B6B6B]' : 'text-gray-400'}>/</span>
            <span className={darkMode ? 'text-[#E5E2DD]' : 'text-gray-900'}>Notifications</span>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="max-w-4xl mx-auto px-6 py-4">
        <div className="flex items-center gap-2 mb-6">
          {['all', 'unread', 'read'].map((f) => (
            <Button
              key={f}
              variant={filter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f)}
              className={filter === f 
                ? 'bg-[#70170F] hover:bg-[#70170F]/90' 
                : darkMode ? 'border-[#2A2A2A] hover:bg-[#1A1A1A]' : ''
              }
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f === 'unread' && unreadCount > 0 && (
                <Badge className="ml-2 bg-[#BC5D41] text-white text-xs">{unreadCount}</Badge>
              )}
            </Button>
          ))}
        </div>

        {/* Notifications List */}
        {loading ? (
          <div className={`text-center py-12 ${darkMode ? 'text-[#6B6B6B]' : 'text-gray-500'}`}>
            <div className="animate-pulse">Loading notifications...</div>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
            <CardContent className="py-12 text-center">
              <Bell className={`w-12 h-12 mx-auto mb-4 ${darkMode ? 'text-[#6B6B6B]' : 'text-gray-300'}`} />
              <h3 className={`text-lg font-medium mb-2 ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
                {filter === 'all' ? 'No notifications yet' : `No ${filter} notifications`}
              </h3>
              <p className={`text-sm ${darkMode ? 'text-[#6B6B6B]' : 'text-gray-500'}`}>
                {filter === 'all' 
                  ? "We'll notify you when something happens"
                  : filter === 'unread' 
                    ? "You're all caught up!"
                    : "No read notifications to show"
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredNotifications.map((notification) => {
              const { icon: Icon, color, bgColor } = config(notification.type);
              
              return (
                <Card 
                  key={notification.id}
                  className={`cursor-pointer transition-all ${
                    darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A] hover:border-[#3A3A3A]' : 'hover:shadow-md'
                  } ${!notification.read ? (darkMode ? 'border-l-4 border-l-[#BC5D41]' : 'border-l-4 border-l-[#70170F]') : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      <div className={`p-3 rounded-lg ${bgColor} flex-shrink-0`}>
                        <Icon className={`w-5 h-5 ${color}`} strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h4 className={`font-medium ${darkMode ? 'text-[#E5E2DD]' : ''} ${!notification.read ? 'font-semibold' : ''}`}>
                              {notification.title}
                            </h4>
                            <p className={`text-sm mt-1 ${darkMode ? 'text-[#8A8A8A]' : 'text-gray-600'}`}>
                              {notification.message}
                            </p>
                            <p className={`text-xs mt-2 ${darkMode ? 'text-[#6B6B6B]' : 'text-gray-400'}`}>
                              {formatTimeAgo(notification.created_at)}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {!notification.read && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={(e) => { e.stopPropagation(); markAsRead(notification.id); }}
                                title="Mark as read"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30"
                              onClick={(e) => { e.stopPropagation(); deleteNotification(notification.id); }}
                              title="Delete"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
